// Dashboard Recruitment section component

import { useNavigate } from "react-router-dom"
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from "recharts"
import {
    FaBriefcase,
    FaUsers,
    FaCalendarCheck,
    FaClock,
} from "react-icons/fa"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Avatar, AvatarFallback } from "@/modules/shared/ui/components/avatar"
import { DashboardStatsDto } from "../../infrastructure/dashboard-service";
import { SectionHeader } from "./SectionHeader";
import { StatCard } from "./StatCard";

import { formatTime, daysAgo } from "../../helpers/helpers"
import { STAGE_COLOURS } from "./COLOR_GROUPS"

export const RecruitmentSection = ({ stats, loading }: { stats: DashboardStatsDto | undefined; loading: boolean }) => {
    const r = stats?.recruitment
    const navigate = useNavigate()

    return (
        <div className="space-y-4">
            <SectionHeader title="Recruitment" description="Vacancies, applications and upcoming interviews" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={FaBriefcase}
                    label="Total Vacancies"
                    value={r?.totalVacancies ?? 0}
                    sub={r ? `${r.activeVacancies} active` : undefined}
                    colour="bg-indigo-500"
                    loading={loading}
                    onClick={() => navigate('/recruitment/vacancies')}
                />
                <StatCard
                    icon={FaUsers}
                    label="Applications"
                    value={r?.totalApplications ?? 0}
                    sub="all stages"
                    colour="bg-violet-500"
                    loading={loading}
                />
                <StatCard
                    icon={FaCalendarCheck}
                    label="Confirmed Interviews"
                    value={r?.upcomingConfirmedInterviews.length ?? 0}
                    sub="upcoming"
                    colour="bg-emerald-500"
                    loading={loading}
                />
                <StatCard
                    icon={FaClock}
                    label="Awaiting Confirmation"
                    value={r?.pendingCandidates.length ?? 0}
                    sub="pending selection"
                    colour="bg-amber-500"
                    loading={loading}
                />
            </div>

            {/* Pipeline chart + upcoming interviews */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-card rounded-xl border border-border shadow-sm p-5">
                    <h4 className="text-sm font-semibold text-foreground mb-4">Application Pipeline</h4>
                    {loading ? (
                        <div className="h-48 flex items-center justify-center">
                            <div className="text-sm text-muted-foreground animate-pulse">Loading chart…</div>
                        </div>
                    ) : r && r.applicationsByStage.some((s) => s.count > 0) ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart
                                data={r.applicationsByStage}
                                margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                                barSize={32}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                                <XAxis
                                    dataKey="stage"
                                    tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    cursor={{ fill: "var(--muted)" }}
                                    contentStyle={{ borderRadius: "8px", border: "1px solid var(--border)", fontSize: "12px", backgroundColor: "var(--popover)", color: "var(--popover-foreground)" }}
                                    formatter={(value: number) => [value, "Applicants"]}
                                />
                                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                    {r.applicationsByStage.map((entry) => (
                                        <Cell key={entry.stage} fill={STAGE_COLOURS[entry.stage] ?? "#6366f1"} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-48 flex flex-col items-center justify-center text-center">
                            <FaUsers className="h-8 w-8 text-muted mb-2" />
                            <p className="text-sm text-muted-foreground">No applications yet</p>
                        </div>
                    )}
                </div>

                {/* Upcoming confirmed interviews */}
                <div className="bg-card rounded-xl border border-border shadow-sm p-5 flex flex-col">
                    <h4 className="text-sm font-semibold text-foreground mb-4">Upcoming Interviews</h4>
                    {loading ? (
                        <div className="space-y-3 flex-1">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
                            ))}
                        </div>
                    ) : r && r.upcomingConfirmedInterviews.length > 0 ? (
                        <ul className="space-y-2 flex-1 overflow-y-auto max-h-56">
                            {r.upcomingConfirmedInterviews.map((interview) => (
                                <li
                                    key={interview.interviewId}
                                    className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100 dark:bg-indigo-500/10 dark:border-indigo-500/20"
                                >
                                    <div className="shrink-0 text-center min-w-9">
                                        <p className="text-[10px] font-semibold text-indigo-500 uppercase leading-tight">
                                            {new Date(interview.startTime).toLocaleDateString("en-GB", { weekday: "short" })}
                                        </p>
                                        <p className="text-lg font-bold text-indigo-700 dark:text-indigo-400 leading-tight">
                                            {new Date(interview.startTime).getDate()}
                                        </p>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-foreground truncate">{interview.candidateName}</p>
                                        <p className="text-xs text-muted-foreground truncate">{interview.vacancyTitle}</p>
                                        <p className="text-xs text-indigo-500 mt-0.5">
                                            {formatTime(interview.startTime)} – {formatTime(interview.endTime)}
                                        </p>
                                    </div>
                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-indigo-100 text-indigo-600 border-0 whitespace-nowrap dark:bg-indigo-500/20 dark:text-indigo-300">
                                        {(interview.startTime)}
                                    </Badge>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                            <FaCalendarCheck className="h-8 w-8 text-muted-foreground/30 mb-2" />
                            <p className="text-sm text-muted-foreground">No upcoming interviews</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Pending candidates */}
            {(loading || (r && r.pendingCandidates.length > 0)) && (
                <div className="bg-card rounded-xl border border-border shadow-sm p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-foreground">Awaiting Interview Confirmation</h4>
                        {!loading && r && (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-0 text-xs dark:bg-amber-500/20 dark:text-amber-300">
                                {r.pendingCandidates.length} pending
                            </Badge>
                        )}
                    </div>
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {r!.pendingCandidates.map((candidate) => (
                                <div
                                    key={candidate.interviewId}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10"
                                >
                                    <div className="relative shrink-0">
                                        <Avatar className="h-9 w-9">
                                            <AvatarFallback className="bg-amber-200 text-amber-700 text-sm font-semibold dark:bg-amber-500/30 dark:text-amber-300">
                                                {candidate.candidateName
                                                    .split(" ")
                                                    .map((n) => n[0])
                                                    .join("")
                                                    .toUpperCase()
                                                    .slice(0, 2)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-400 border-2 border-card" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-foreground truncate">{candidate.candidateName}</p>
                                        <p className="text-xs text-muted-foreground truncate">{candidate.vacancyTitle}</p>
                                        <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                                            {candidate.slotsOffered} slot{candidate.slotsOffered !== 1 ? "s" : ""} offered · sent {daysAgo(candidate.slotsOfferedAt)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-3">
                        These candidates have received interview invitations but have not yet selected a time slot. They will be removed once they confirm.
                    </p>
                </div>
            )}
        </div>
    )
}